// Path: src/components/aols/aeo/OLSCalendarTime/OLSCalendarTime.tsx
// Updated to ensure all text in widget section is white (#ffffff) for high contrast, retain visibility in 'HTML Widget for Personal Website Use' collapsed section, fix footer and link issues, and enhance SEO
// Ensures single footer rendering in PublicLayout.tsx
import React, { useCallback } from "react";
import { Layout, Button, Typography, Tooltip, Collapse } from "antd";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { HomeOutlined } from "@ant-design/icons";
import logo from "/logo.jpg";
import { publicImages } from "../../../auth/constants/PublicImg";
import OLSCalendar from "../../../auth/components/OLSCalendar";
import LightTime from "./LightTime";
import styled from "styled-components";
import "./OLSCalendarTime.css";

const { Title, Paragraph } = Typography;
const { Panel } = Collapse;

// Define the type for an image object in publicImages
interface PublicImage {
  url: string;
  alt: string;
}

// Define the type for the publicImages object
interface PublicImages {
  getImageById: (id: number) => PublicImage;
}

// Styled components for breadcrumbs, yellow borders, widget code, and text link
const BreadcrumbWrapper = styled.div`
  position: absolute;
  top: 10px;
  left: 20px;
  z-index: 1001;

  .ant-breadcrumb a {
    color: #ffcc00;
    transition: color 0.3s ease;
  }

  .ant-breadcrumb a:hover {
    color: #ffaa00;
  }

  @media (max-width: 768px) {
    left: 10px;
    top: 5px;
    .ant-breadcrumb {
      font-size: 12px;
    }
  }
`;

const CustomBreadcrumb = styled.div`
  margin-bottom: 20px;
  text-align: left;
  font-size: 14px;
  line-height: 1.5714285714285714;

  .breadcrumb-item {
    display: inline-block;
  }

  .breadcrumb-link {
    color: #ffaa00 !important;
    text-decoration: none;
  }

  .breadcrumb-link:hover {
    color: #fce248 !important;
  }

  .breadcrumb-separator {
    color: #fff;
    margin: 0 8px;
  }

  .breadcrumb-non-active {
    color: #fff !important;
  }
`;

const FeatureCard = styled.div`
  background: #1a1a1a !important;
  padding: 20px;
  margin: 15px 0;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  text-align: left;
  border: 2px solid #ffcc00;
`;

const WidgetCodeWrapper = styled.pre`
  background: #1a1a1a;
  color: #ffffff;
  padding: 15px;
  border-radius: 8px;
  overflow-x: auto;
  font-family: 'Courier New', Courier, monospace;
  font-size: 14px;
  margin-top: 20px;
  border: 1px solid #ffcc00;
`;

const CommercialLink = styled.a`
  color: #ffffff;
  text-decoration: none;
  font-size: 14px;
  display: inline;

  &:hover {
    text-decoration: underline;
    color: #fce248;
  }
`;

const OLSCalendarTime: React.FC = () => {
  const handleLogoClick = useCallback(() => {
    window.location.href = "https://www.olsme.com";
  }, []);

  // Embeddable widget code snippet with redirect
  const widgetCode = `
<div id="ols-calendar-widget"></div>
<link rel="stylesheet" href="https://www.olsme.com/widgets/ols-calendar-widget.css">
<script src="https://www.olsme.com/widgets/ols-calendar-widget.js"></script>
  `.trim();

  return (
    <Layout className="full-height-layout" style={{ background: "#000000" }}>
      <Helmet>
        <title>OLS Calendar Time | Free HTML Calendar Widget for Personal Use</title>
        <meta
          name="description"
          content="Get the free OLS Sun Light Calendar HTML widget for personal websites, linking to olsme.com. Contact contact@olsme.com for commercial use. Align with Sun rhythms to enhance meditation and well-being. Join OLS today!"
        />
        <meta
          name="keywords"
          content="OLS Sun Light Calendar, Light Time, Calendar Synchronization, Sun Light Meditation, human consciousness, OneLightSystem OLS, meditation techniques, Light Year Age, timekeeping synchronization, spiritual growth, sustainable living, free OLS calendar widget, HTML calendar widget, Sun Light Calendar for personal use, OLS personal website widget"
        />
        <meta name="author" content="OneLightSystem OLS" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="OLS Calendar Time | Free HTML Calendar Widget for Personal Use" />
        <meta
          property="og:description"
          content="Embed the free OLS Sun Light Calendar HTML widget on personal websites, linking to olsme.com. Contact contact@olsme.com for commercial use to enhance meditation with Sun rhythms. Join OLS today."
        />
        <meta property="og:url" content="https://www.olsme.com/OLScalendarTime" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.olsme.com/logo.jpg" />
        <meta property="og:image:alt" content="OneLightSystem OLS Logo" />
        <meta property="og:site_name" content="OneLightSystem OLS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="OLS Calendar Time | Free HTML Calendar Widget for Personal Use" />
        <meta
          name="twitter:description"
          content="Free OLS Sun Light Calendar HTML widget for personal sites, linking to olsme.com. Contact contact@olsme.com for commercial use to align with Sun rhythms. Join OLS today."
        />
        <meta name="twitter:image" content="https://www.olsme.com/logo.jpg" />
        <meta name="twitter:image:alt" content="OneLightSystem OLS Logo" />
        <meta name="twitter:site" content="@onelightsystem" />
        <meta name="twitter:creator" content="@onelightsystem" />
        <link rel="canonical" href="https://www.olsme.com/OLScalendarTime" />
        <script type="application/ld+json">
          {JSON.stringify([
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              name: "OneLightSystem OLS",
              url: "https://www.olsme.com",
              subOrganization: {
                "@type": "EducationalOrganization",
                name: "OLS Calendar Time",
                url: "https://www.olsme.com/OLScalendarTime",
                description: "OLS Calendar Time offers the Sun Light Calendar, Light Time Synchronization, and a free HTML widget for personal use (links to olsme.com) or commercial licensing via contact@olsme.com.",
                founder: {
                  "@type": "Person",
                  name: "Nazar Pankiv",
                  jobTitle: "CEO"
                },
                contactPoint: {
                  "@type": "ContactPoint",
                  contactType: "customer service",
                  email: "contact@olsme.com"
                }
              },
              address: {
                "@type": "PostalAddress",
                streetAddress: "2033 Ralston Ave.",
                addressLocality: "Belmont",
                addressRegion: "CA",
                postalCode: "94002",
                addressCountry: "US"
              },
              sameAs: [
                "https://www.olsme.com",
                "https://www.ols-med.net",
                "https://www.facebook.com/onelightsystemols",
                "https://www.linkedin.com/company/one-light-system",
                "https://www.youtube.com/@ONELIGHTSYSTEMOLSMeditation",
                "https://x.com/onelightsystem"
              ]
            },
            {
              "@context": "https://schema.org",
              "@type": "SoftwareApplication",
              name: "OLS Sun Light Calendar Widget",
              description: "A free HTML widget for personal websites displaying the OLS Sun Light Calendar day count, linking to olsme.com. Contact contact@olsme.com for commercial use.",
              applicationCategory: "Utility",
              operatingSystem: "Web",
              offers: {
                "@type": "Offer",
                price: "0",
                priceCurrency: "USD",
                availability: "https://schema.org/OnlineOnly",
                description: "Free for personal use with a link to olsme.com; commercial use requires contacting OLS."
              },
              url: "https://www.olsme.com/OLScalendarTime"
            },
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: "https://www.olsme.com"
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "OLS iee.aeo",
                  item: "https://www.olsme.com/OLSaeo"
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: "OLS Calendar Time",
                  item: "https://www.olsme.com/OLScalendarTime"
                }
              ]
            }
          ])}
        </script>
      </Helmet>

      <BreadcrumbWrapper>
        <CustomBreadcrumb>
          <span className="breadcrumb-item">
            <Link to="/" className="breadcrumb-link" aria-label="Navigate to Home">
              <HomeOutlined /> Home
            </Link>
          </span>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-item">
            <Link to="/OLSaeo" className="breadcrumb-link" aria-label="Navigate to OLS iee.aeo">
              OLS iee.aeo
            </Link>
          </span>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-item breadcrumb-non-active">
            OLS Calendar Time
          </span>
        </CustomBreadcrumb>
      </BreadcrumbWrapper>

      <Layout.Header
        className="content-section"
        style={{
          textAlign: "center",
          padding: "40px 20px",
          background: "rgba(255, 204, 0, 0.1)",
          position: "relative",
          zIndex: 100,
        }}
      >
        <Tooltip title="Visit OLS Main Site">
          <Link to="/" onClick={handleLogoClick}>
            <img
              src={logo}
              alt="OneLightSystem OLS Logo"
              className="logo"
              aria-label="OLS Logo"
              loading="lazy"
            />
          </Link>
        </Tooltip>
        <Title level={1} className="section-title" style={{ marginBottom: "20px" }}>
          OLS Calendar Time
        </Title>
      </Layout.Header>

      <Layout.Content className="content-section">
        <div className="content">
          <Paragraph>
            <Link
              to="/OLSaeo"
              style={{ fontSize: "21px", color: "#ffaa00" }}
              aria-label="Learn about OneLightSystem OLS iee.aeo"
            >
              OneLightSystem OLS iee.aeo
            </Link>
          </Paragraph>
          <div className="button-row">
            <Button className="nav-button">
              <Link to="/lightScience" aria-label="Learn about OneLightSystem">OneLightSystem</Link>
            </Button>
            <Button className="nav-button">
              <Link to="/OLSaeo" aria-label="Explore OLS iee.aeo">OLS iee.aeo</Link>
            </Button>
            <Button className="nav-button">
              <Link to="/OLShealth" aria-label="Learn about OLS Health">OLS Health</Link>
            </Button>
            <Button className="nav-button">
              <Link to="/OLSinventions" aria-label="Explore OLS Inventions">OLS Inventions</Link>
            </Button>
            <Button className="nav-button">
              <Link to="/aOLSLMasset" aria-label="Explore OLSLM Asset">OLSLM Asset</Link>
            </Button>
            <Button className="nav-button">
              <Link to="/olsme" aria-label="Learn about OLSME">OLSME</Link>
            </Button>
          </div>

          <FeatureCard>
            <Title level={3} className="subsection-title">
              A Sun Calendar
            </Title>
            <div className="invention-section">
              <div className="description-text content-text">
                OneLightSystem OLS iee.aeo has adjusted and developed the Sun Light Calendar, aligning with Sun Source Energy, fostering human consciousness evolution:
                <div className="calendar-wrapper">
                  <OLSCalendar />
                </div>
                <ul className="styled-list">
                  <li>
                    <strong>A Correct New Year:</strong> The first day of the new year begins on December 22, 2024, marking the start of a new cycle.
                  </li>
                  <li>
                    <strong>An Equinox:</strong> Equinoxes are events that occur twice a year when day and night are of equal length.
                  </li>
                  <li>
                    <strong>The Longest Day:</strong> The longest day of the year, when the Sun reaches its highest point in the sky.
                  </li>
                  <li>
                    <strong>Light Year Age:</strong> What year is it now? We begin counting from the first known Sun Light Meditation pre-Founder Akhenaten’s birth, 3405 years ago. So today is the 3405th Year of the Light Year Age.
                  </li>
                </ul>
                <Button className="custom-button">
                  <Link to="/Download" aria-label="Download the Light Calendar 8.0">Download the Light Calendar 8.0</Link>
                </Button>
              </div>
              <img
                className="invention-image"
                src={publicImages.getImageById(10).url}
                alt={publicImages.getImageById(10).alt}
                loading="lazy"
              />
            </div>
          </FeatureCard>

          <Collapse defaultActiveKey={["1"]} style={{ margin: "20px 0", background: "#1a1a1a", borderColor: "#ffcc00" }}>
            <Panel header="HTML Widget for Personal Website Use" key="1" style={{ background: "#1a1a1a", color: "#ffffff" }}>
              <FeatureCard>
                <Title level={4} className="subsection-title">
                  OLS Sun Light Calendar Widget
                </Title>
                <Paragraph className="content-text">
                  Embed the OLS Sun Light Calendar day count on your personal website for free, linking to olsme.com as part of our exchange benefits agreement. For commercial use, please contact us at <CommercialLink href="https://www.olsme.com/contact" target="_blank" rel="noopener noreferrer" aria-label="Contact OLS for commercial use">olsme.com/contact</CommercialLink>.
                </Paragraph>
                <div style={{ textAlign: "center", margin: "20px 0" }}>
                  <a href="https://www.olsme.com" target="_blank" rel="noopener noreferrer" aria-label="Visit OLS website">
                    <OLSCalendar />
                  </a>
                </div>
                <Paragraph className="content-text">
                  Copy the code below and paste it into your website’s HTML to add the widget:
                </Paragraph>
                <WidgetCodeWrapper aria-label="OLS Calendar Widget embed code">
                  {widgetCode}
                </WidgetCodeWrapper>
              </FeatureCard>
            </Panel>
          </Collapse>

          <FeatureCard>
            <Title level={3} className="subsection-title">
              Light Time Measurement
            </Title>
            <div className="invention-section">
              <div className="description-text content-text">
                OLS iee pioneers a revolutionary timekeeping system synchronized with Sun rhythms:
                <ul className="styled-list">
                  <li>
                    <strong>Light Time:</strong> A proper clock. This innovation aligns human activity with natural light cycles, enhancing meditation efficacy, biological clocks, and well-being. Open to all—request access to integrate it into your life.
                  </li>
                </ul>
                <LightTime />
                <Button className="custom-button">
                  <Link to="/contact" aria-label="Request access to Light Time">Request Access</Link>
                </Button>
              </div>
              <img
                className="invention-image"
                src={publicImages.getImageById(7).url}
                alt={publicImages.getImageById(7).alt}
                loading="lazy"
              />
            </div>
          </FeatureCard>

          <div className="button-group">
            <Tooltip title="OLS Student Registration">
              <Button className="custom-button">
                <Link to="/register/olssregister" aria-label="Register as OLS Student">OLS Student</Link>
              </Button>
            </Tooltip>
            <Tooltip title="OLSME Subscription">
              <Button className="custom-button">
                <Link to="/register/olsmeregister" aria-label="Subscribe to OLSME">OLSME Subscription</Link>
              </Button>
            </Tooltip>
            <Tooltip title="OLSLM Assets">
              <Button className="custom-button">
                <Link to="/register/olslmregister" aria-label="Explore OLSLM Assets">OLSLM Assets</Link>
              </Button>
            </Tooltip>
          </div>
        </div>
      </Layout.Content>
      {/* Removed <Layout.Footer><Footer /></Layout.Footer> to prevent duplication; footer is rendered in PublicLayout.tsx */}
    </Layout>
  );
};

export default OLSCalendarTime;